import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { useReportScope } from '../../lib/useReportScope';
import { formatHoursMinutes } from '../../lib/format';
import ReportPdfButton, { ExcelIcon, PdfIcon, excelBtnStyle, downloadExcel } from '../../components/workspace/ReportPdfButton';
import { SearchableSelect } from '../../components/workspace/filters/FilterDropdowns';

function downloadCombinedExcel(filename, subtitle, areasPdfProps, routinesPdfProps) {
  const esc = v => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const sectionBadge = (label, color, bgColor) =>
    `<tr><td colspan="10" style="padding:14px 12px 6px;border:none;font-size:13px;font-weight:700;color:${color};background:${bgColor};letter-spacing:0.04em;">${esc(label)}</td></tr>`;

  const subtitleRow = subtitle
    ? `<tr><td colspan="10" style="padding:2px 12px 10px;border:none;font-size:11px;color:#444;text-align:center;">${esc(subtitle)}</td></tr>`
    : '';

  const headerRow = (headers) =>
    `<tr>${headers.map(h => `<th style="background:#2563eb;color:#fff;font-weight:700;padding:8px 12px;border:1px solid #1d4ed8;white-space:nowrap;font-size:12px;text-transform:uppercase;">${esc(h)}</th>`).join('')}</tr>`;

  const bodyRows = (rows, colCount) =>
    rows.map((row, ri) => {
      const bg = ri % 2 === 0 ? '#ffffff' : '#f1f5f9';
      const cells = row.map(cell => `<td style="background:${bg};padding:6px 12px;border:1px solid #e2e8f0;white-space:nowrap;font-size:13px;">${esc(cell)}</td>`).join('');
      return `<tr>${cells}</tr>`;
    }).join('');

  let body = '';

  if (areasPdfProps) {
    body += sectionBadge('⊞  AREAS REPORT', '#0369a1', '#eff6ff');
    body += subtitleRow;
    body += `<tr><td colspan="10" style="padding:3px;border:none;"></td></tr>`;
    body += headerRow(areasPdfProps.table.headers);
    body += bodyRows(areasPdfProps.table.rows, areasPdfProps.table.headers.length);
    body += `<tr><td colspan="10" style="padding:10px;border:none;"></td></tr>`;
  }

  if (routinesPdfProps) {
    body += sectionBadge('≡  ROUTINES REPORT', '#15803d', '#f0fdf4');
    body += subtitleRow;
    body += `<tr><td colspan="10" style="padding:3px;border:none;"></td></tr>`;
    body += headerRow(routinesPdfProps.table.headers);
    body += bodyRows(routinesPdfProps.table.rows, routinesPdfProps.table.headers.length);
  }

  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office"
    xmlns:x="urn:schemas-microsoft-com:office:excel"
    xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8">
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
<x:Name>Other Reports</x:Name><x:WorksheetOptions><x:DisplayGridlines/>
</x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head><body>
<table border="0" cellspacing="0" style="font-family:Calibri,Arial,sans-serif;border-collapse:collapse;">
<tbody>${body}</tbody>
</table></body></html>`;

  const blob = new Blob(['﻿' + html], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const STATUS_META = {
  completed:      { label: 'Completed',   color: '#4ade80' },
  pending:        { label: 'In Progress', color: '#fbbf24' },
  not_started:    { label: 'Not Started', color: '#f87171' },
  not_applicable: { label: 'N/A',         color: '#94a3b8' },
};

const PERIOD_LABEL = {
  daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly',
  quarterly: 'Quarterly', yearly: 'Yearly',
};

function fmtHours(minutes) {
  const n = Number(minutes);
  if (!Number.isFinite(n)) return '—';
  return formatHoursMinutes(n);
}

function nowMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
}

function monthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    opts.push({ value: val, label: d.toLocaleString('default', { month: 'long', year: 'numeric' }) });
  }
  return opts;
}

function HoursBar({ minutes, maxMinutes }) {
  const pct = maxMinutes > 0 ? Math.min(100, (minutes / maxMinutes) * 100) : 0;
  const color = pct >= 70 ? '#4ade80' : pct >= 35 ? '#38bdf8' : pct > 0 ? '#fbbf24' : '#374151';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: 'rgba(128,128,128,0.15)', borderRadius: 3, overflow: 'hidden', minWidth: 80 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 48, textAlign: 'right' }}>
        {fmtHours(minutes)}
      </span>
    </div>
  );
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, color: '#6b7280' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      background: `${m.color}22`, color: m.color,
    }}>
      {m.label}
    </span>
  );
}

function CompletionBar({ pct }) {
  if (pct == null) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const color = pct === 100 ? '#4ade80' : pct >= 50 ? '#fbbf24' : '#f87171';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: 'rgba(128,128,128,0.15)', borderRadius: 3, overflow: 'hidden', minWidth: 80 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 32, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

function Th({ children, right }) {
  return (
    <th style={{
      padding: '10px 14px', textAlign: 'center',
      fontWeight: 600, color: 'var(--text-secondary)',
      whiteSpace: 'nowrap', fontSize: 12,
      textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>
      {children}
    </th>
  );
}

function StepCircle({ num }) {
  return (
    <div style={{
      width: 26, height: 26, borderRadius: '50%', background: 'var(--brand)',
      color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {num}
    </div>
  );
}

// Role/simple single-select dropdown
function SimpleSelect({ value, onChange, options, placeholder, loading, disabled }) {
  return (
    <SearchableSelect
      value={value}
      onChange={onChange}
      options={options}
      placeholder={loading ? 'Loading...' : placeholder}
      showAll={false}
      disabled={disabled || loading}
      btnClass="form-select-btn"
      matchTriggerWidth
    />
  );
}

// Multi-select dropdown with chips and "All" option
function MultiSelectDropdown({ label, items, selected, onToggle, onSelectAll, onClear, onSetSelected, loading, emptyMsg, idKey = 'id', nameKey = 'name', allLabel = 'All', allowAll = false, allSelected = false, onToggleAll }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = items.filter(i =>
    (i[nameKey] || '').toLowerCase().includes(search.toLowerCase())
  );

  const displayLabel = allSelected ? allLabel : selected.length === 0
    ? `Select ${label}...`
    : selected.map(id => {
      const item = items.find(i => i[idKey] === id);
      return item ? item[nameKey] : id;
    }).join(', ');

  const isOverflow = !allSelected && selected.length > 2;
  const chipNames = !allSelected && selected.length > 0
    ? selected.slice(0, 2).map(id => {
      const item = items.find(i => i[idKey] === id);
      return item ? item[nameKey] : id;
    })
    : [];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        onClick={() => !loading && setOpen(o => !o)}
        style={{
          minHeight: 38, padding: '4px 10px 4px 10px', borderRadius: 8,
          border: '1px solid var(--border)', cursor: loading ? 'not-allowed' : 'pointer',
          background: 'var(--bg-input,var(--bg-muted))',
          display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap',
        }}
      >
        {allSelected ? (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: 'var(--brand)', color: '#fff',
            fontSize: 12, fontWeight: 500, padding: '2px 10px', borderRadius: 12,
          }}>
            {allLabel}
            <span onClick={e => { e.stopPropagation(); onToggleAll && onToggleAll(); }} style={{ cursor: 'pointer', opacity: 0.8 }}>×</span>
          </span>
        ) : selected.length === 0 ? (
          <span style={{ fontSize: 13, color: 'var(--text-muted)', flex: 1 }}>
            {loading ? 'Loading...' : `Select ${label}...`}
          </span>
        ) : (
          <>
            {chipNames.map((name, i) => (
              <span key={selected[i]} style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                background: 'var(--brand)', color: '#fff',
                fontSize: 12, fontWeight: 500, padding: '2px 8px', borderRadius: 12,
              }}>
                {name}
                <span onClick={e => { e.stopPropagation(); onToggle(selected[i]); }} style={{ cursor: 'pointer', opacity: 0.8 }}>×</span>
              </span>
            ))}
            {isOverflow && (
              <span style={{
                display: 'inline-flex', alignItems: 'center',
                background: 'rgba(var(--brand-rgb,6,182,212),0.15)', color: 'var(--brand)',
                fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
              }}>
                +{selected.length - 2} more
              </span>
            )}
          </>
        )}
        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 11 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          marginTop: 4, minWidth: 200,
        }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
            <input
              autoFocus
              placeholder={`Search ${label}...`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
              style={{
                width: '100%', padding: '6px 10px', borderRadius: 6,
                border: '1px solid var(--border)', fontSize: 12,
                background: 'var(--bg-input,var(--bg-muted))', color: 'var(--text-primary)',
                outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {allowAll && (
              <label style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)',
                background: allSelected ? 'rgba(6,182,212,0.08)' : 'transparent',
              }}>
                <input type="checkbox" checked={allSelected} onChange={() => onToggleAll && onToggleAll()}
                  style={{ accentColor: 'var(--brand)', width: 15, height: 15 }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{allLabel}</span>
              </label>
            )}
            {loading ? (
              <div style={{ padding: '12px 14px', color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '12px 14px', color: 'var(--text-muted)', fontSize: 13 }}>{emptyMsg || `No ${label} found.`}</div>
            ) : filtered.map(item => {
              const isChecked = allSelected || selected.includes(item[idKey]);
              return (
                <label key={item[idKey]} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', cursor: 'pointer',
                  background: isChecked ? 'rgba(6,182,212,0.06)' : 'transparent',
                }}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => {
                      if (allSelected) {
                        // deselect "All", keep everyone except this one selected
                        onToggleAll && onToggleAll();
                        const others = items.filter(i => i[idKey] !== item[idKey]).map(i => i[idKey]);
                        onSetSelected && onSetSelected(others);
                      } else {
                        onToggle(item[idKey]);
                      }
                    }}
                    style={{ accentColor: 'var(--brand)', width: 15, height: 15, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{item[nameKey]}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Item panel with search + select all
function ItemPanel({ title, icon, color, items, selected, onToggle, onSelectAll, onClearAll, loading, nameKey = 'name', highlight = false }) {
  const [search, setSearch] = useState('');
  const filtered = items.filter(i => (i[nameKey] || '').toLowerCase().includes(search.toLowerCase()));
  const allSelected = items.length > 0 && items.every(i => selected.includes(i.id));

  return (
    <div style={{
      flex: 1, minWidth: 0,
      background: 'var(--bg-card)',
      border: highlight ? '2px solid #fbbf24' : '1px solid var(--border)',
      borderRadius: 10, overflow: 'hidden',
      transition: 'border-color 0.2s',
      boxShadow: highlight ? '0 0 0 3px rgba(251,191,36,0.15)' : 'none',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', borderBottom: '1px solid var(--border)',
        background: 'var(--bg-muted)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color }}>{icon}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            fontSize: 12, fontWeight: 600, color,
            background: `${color}18`,
            padding: '2px 10px', borderRadius: 12,
          }}>
            {selected.length} Selected
          </span>
        </div>
      </div>

      {/* Search + controls */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 12px', borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', opacity: 0.4 }}>
            <circle cx="5.5" cy="5.5" r="4.5" stroke="currentColor" strokeWidth="1.3"/>
            <path d="M9 9l2.5 2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
          <input
            placeholder={`Search ${title.toLowerCase()}...`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '6px 10px 6px 28px', borderRadius: 6,
              border: '1px solid var(--border)', fontSize: 12,
              background: 'var(--bg-input,var(--bg-muted))', color: 'var(--text-primary)',
              outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>
        <button
          onClick={allSelected ? onClearAll : onSelectAll}
          style={{
            padding: '5px 12px', borderRadius: 6, border: `1px solid ${color}44`,
            background: 'transparent', color, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {allSelected ? 'Clear All' : 'Select All'}
        </button>
      </div>

      {/* List */}
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: '16px 14px', color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '16px 14px', color: 'var(--text-muted)', fontSize: 13 }}>
            {items.length === 0 ? `No ${title.toLowerCase()} found.` : 'No results.'}
          </div>
        ) : filtered.map(item => {
          const isChecked = selected.includes(item.id);
          return (
            <label key={item.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '9px 14px', cursor: 'pointer',
              borderBottom: '1px solid var(--border)',
              background: isChecked ? `${color}09` : 'transparent',
              transition: 'background 0.1s',
            }}>
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => onToggle(item.id)}
                style={{ accentColor: color, width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
              />
              <span style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.4 }}>{item[nameKey]}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// Donut chart (SVG, no library)
function DonutChart({ areas, routines }) {
  const size = 160;
  const stroke = 26;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;

  const aTotal = areas?.total || 0;
  const rTotal = routines?.total || 0;
  const grand = aTotal + rTotal;

  const aFrac = grand > 0 ? aTotal / grand : (areas ? 1 : 0);
  const rFrac = grand > 0 ? rTotal / grand : (routines ? 1 : 0);

  const gap = grand > 0 && aTotal > 0 && rTotal > 0 ? 0.02 : 0;

  const aDash  = (aFrac - gap) * circ;
  const aSpace = circ - aDash;
  const rDash  = (rFrac - gap) * circ;
  const rSpace = circ - rDash;

  const aOffset = 0;
  const rOffset = -(aFrac * circ);

  const aColor = '#38bdf8';
  const rColor = '#4ade80';

  const hasData = areas || routines;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
        {/* Track */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(128,128,128,0.12)" strokeWidth={stroke} />
        {hasData ? (
          <>
            {areas && (
              <circle
                cx={cx} cy={cy} r={r} fill="none"
                stroke={aColor} strokeWidth={stroke}
                strokeDasharray={`${Math.max(0, aDash)} ${aSpace}`}
                strokeDashoffset={-aOffset * circ}
                strokeLinecap="butt"
              />
            )}
            {routines && (
              <circle
                cx={cx} cy={cy} r={r} fill="none"
                stroke={rColor} strokeWidth={stroke}
                strokeDasharray={`${Math.max(0, rDash)} ${rSpace}`}
                strokeDashoffset={rOffset}
                strokeLinecap="butt"
              />
            )}
          </>
        ) : (
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(128,128,128,0.12)" strokeWidth={stroke} />
        )}
      </svg>
      {/* Legend — right of chart */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {areas && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, background: aColor, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Areas Completed</span>
            </div>
            <span style={{ fontSize: 18, fontWeight: 700, color: aColor, paddingLeft: 18 }}>
              {areas.completed != null ? `${Math.round(aFrac * 100)}% (${areas.completed})` : '—'}
            </span>
          </div>
        )}
        {routines && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, background: rColor, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Routines Completed</span>
            </div>
            <span style={{ fontSize: 18, fontWeight: 700, color: rColor, paddingLeft: 18 }}>
              {routines.completed != null ? `${Math.round(rFrac * 100)}% (${routines.completed})` : '—'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ icon, label, value, color }) {
  return (
    <div style={{
      flex: '1 1 calc(50% - 6px)', maxWidth: 'calc(50% - 6px)',
      background: 'var(--bg-muted)',
      border: '1px solid var(--border)', borderRadius: 10,
      padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: 10,
        background: `${color}18`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <span style={{ color, fontSize: 20 }}>{icon}</span>
      </div>
      <div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>{value}</div>
      </div>
    </div>
  );
}

// Renders a single PDF button that combines areas + routines into one document
function CombinedPdfButton({ areasPdfProps, routinesPdfProps, subtitle, requireSelection }) {
  const { organisation } = useWorkspace();

  const handleDownload = async () => {
    const { jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const orgName = organisation?.name || '';
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 14;
    let y = margin;

    const nowLabel = () => new Date().toLocaleString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });

    // Page header (org name, title, subtitle, generated time) — drawn once
    doc.setFillColor(6, 182, 212);
    doc.rect(0, 0, pageW, 1.5, 'F');
    y = 8;
    doc.setFontSize(14).setFont('helvetica', 'bold').setTextColor(30, 30, 30);
    doc.text(orgName, margin, y + 6);
    y += 12;
    doc.setFontSize(11).setFont('helvetica', 'bold').setTextColor(6, 182, 212);
    doc.text('OTHER REPORTS', margin, y);
    y += 5;
    if (subtitle) {
      doc.setFontSize(9).setFont('helvetica', 'normal').setTextColor(100, 100, 100);
      doc.text(subtitle, margin, y);
      y += 5;
    }
    doc.setFontSize(8).setFont('helvetica', 'italic').setTextColor(150, 150, 150);
    doc.text(`Generated: ${nowLabel()}`, pageW - margin, 10, { align: 'right' });
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y, pageW - margin, y);
    y += 6;

    const drawSection = (props, label, headerColor) => {
      if (!props) return;
      // Section badge row
      doc.setFontSize(9).setFont('helvetica', 'bold').setTextColor(...headerColor);
      doc.text(label.toUpperCase(), margin, y);
      y += 6;
      const body = (props.table.rows || []).map(row => row.map(cell => (cell == null ? '—' : String(cell))));
      autoTable(doc, {
        startY: y,
        head: [props.table.headers],
        body,
        margin: { left: margin, right: margin },
        styles: { fontSize: 9, cellPadding: { top: 5, right: 7, bottom: 5, left: 7 }, textColor: [30,30,30], lineColor: [220,220,220], lineWidth: 0.1 },
        headStyles: { fillColor: headerColor, textColor: [255,255,255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        didDrawPage: (data) => {
          doc.setFontSize(7).setFont('helvetica', 'normal').setTextColor(150,150,150);
          doc.text(`${orgName} — Other Reports`, margin, doc.internal.pageSize.getHeight() - 6);
          doc.text(`Page ${data.pageNumber}`, pageW - margin, doc.internal.pageSize.getHeight() - 6, { align: 'right' });
        },
      });
      y = doc.lastAutoTable.finalY + 14;
    };

    drawSection(areasPdfProps, '⊞  Areas Report', [56, 189, 248]);
    drawSection(routinesPdfProps, '≡  Routines Report', [74, 222, 128]);

    doc.save(`Other-Reports-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <button
      onClick={() => requireSelection ? requireSelection(handleDownload) : handleDownload()}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '7px 14px', borderRadius: 7,
        border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b',
        fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'opacity 0.15s',
      }}
    >
      <PdfIcon /> PDF
    </button>
  );
}

export default function OtherReports() {
  const { appId } = useParams();
  const api = useApi();
  const { me } = useWorkspace();
  const { isAdmin, isManager } = useReportScope(appId);
  const isEmployee = !isAdmin && !isManager;

  // Filters
  const [selectedRole, setSelectedRole] = useState('');
  const [allEmployees, setAllEmployees] = useState(false); // "All" toggle for employees
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [reportType, setReportType] = useState({ areas: true, routines: true });
  const [month, setMonth] = useState(nowMonth());

  // Data
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roleEmployees, setRoleEmployees] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [areaList, setAreaList] = useState([]);
  const [routineList, setRoutineList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedRoutines, setSelectedRoutines] = useState([]);

  // Report output
  const [reportData, setReportData] = useState(null);
  const [routineReportData, setRoutineReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const MONTHS = useMemo(monthOptions, []);

  // Load roles on mount
  useEffect(() => {
    setRolesLoading(true);
    api.listReportRoles(appId)
      .then(r => setRoles(r.items || []))
      .catch(() => {})
      .finally(() => setRolesLoading(false));
  }, [appId]);

  // When role changes: load employees and areas/routines
  useEffect(() => {
    setAllEmployees(false);
    setSelectedEmployees([]);
    setSelectedAreas([]);
    setSelectedRoutines([]);
    setReportData(null);
    setRoutineReportData(null);
    setError(null);
    setRoleEmployees([]);
    setAreaList([]);
    setRoutineList([]);

    if (!selectedRole) return;
    const roleId = Number(selectedRole);

    setEmpLoading(true);
    api.listReportEmployeesByRole(appId, roleId)
      .then(r => {
        const emps = r.items || [];
        setRoleEmployees(emps);
        if (isEmployee && me?.id) {
          setSelectedEmployees([Number(me.id)]);
        }
      })
      .catch(() => {})
      .finally(() => setEmpLoading(false));

    setListLoading(true);
    const promises = [];
    if (reportType.areas)    promises.push(api.listReportAreas(appId,    { role_id: roleId }).then(r => setAreaList(r.items || [])));
    if (reportType.routines) promises.push(api.listReportRoutines(appId, { role_id: roleId }).then(r => setRoutineList(r.items || [])));
    Promise.all(promises).catch(() => {}).finally(() => setListLoading(false));
  }, [appId, selectedRole, isEmployee, me?.id]);

  // When reportType changes, reload lists for current role
  useEffect(() => {
    if (!selectedRole) return;
    const roleId = Number(selectedRole);
    setSelectedAreas([]);
    setSelectedRoutines([]);
    setReportData(null);
    setRoutineReportData(null);
    setListLoading(true);
    const promises = [];
    if (reportType.areas)    promises.push(api.listReportAreas(appId,    { role_id: roleId }).then(r => setAreaList(r.items || [])));
    if (reportType.routines) promises.push(api.listReportRoutines(appId, { role_id: roleId }).then(r => setRoutineList(r.items || [])));
    Promise.all(promises).catch(() => {}).finally(() => setListLoading(false));
  }, [appId, reportType.areas, reportType.routines]);

  function toggleEmployee(id) {
    if (isEmployee) return;
    setAllEmployees(false);
    setSelectedEmployees(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  function toggleAllEmployees() {
    if (isEmployee) return;
    setAllEmployees(prev => {
      if (!prev) setSelectedEmployees([]);
      return !prev;
    });
  }

  const empName = u => [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;

  const loadReport = useCallback(async () => {
    if (!month || !selectedRole) return;
    setLoading(true);
    setError(null);
    setReportData(null);
    setRoutineReportData(null);
    try {
      // Determine user IDs + names to fetch
      let userList; // [{ id, name, roleName }]
      const roleName = roles.find(r => String(r.id) === String(selectedRole))?.title || '';
      if (isEmployee) {
        const name = me ? [me.first_name, me.last_name].filter(Boolean).join(' ') || me.email : 'You';
        userList = me?.id ? [{ id: Number(me.id), name, roleName }] : [];
      } else if (allEmployees) {
        userList = roleEmployees.map(u => ({
          id: Number(u.id),
          name: empName(u),
          roleName,
        }));
        if (userList.length === 0 && me?.id) {
          const name = me ? [me.first_name, me.last_name].filter(Boolean).join(' ') || me.email : 'You';
          userList = [{ id: Number(me.id), name, roleName }];
        }
      } else {
        userList = selectedEmployees.map(id => {
          const u = roleEmployees.find(e => Number(e.id) === id);
          return { id, name: u ? empName(u) : `User #${id}`, roleName };
        });
        if (userList.length === 0 && me?.id) {
          const name = me ? [me.first_name, me.last_name].filter(Boolean).join(' ') || me.email : 'You';
          userList = [{ id: Number(me.id), name, roleName }];
        }
      }

      const areaIds = selectedAreas.length > 0 ? selectedAreas.join(',') : undefined;
      const routineIds = selectedRoutines.length > 0 ? selectedRoutines.join(',') : undefined;

      if (reportType.areas) {
        const results = await Promise.all(
          userList.map(u => api.getAreaReport(appId, { month, user_id: u.id, ...(areaIds ? { area_ids: areaIds } : {}) })
            .then(r => ({ user: u, items: r.items || [] }))
          )
        );
        // Flat rows: one per employee × area
        const rows = [];
        for (const { user, items } of results) {
          for (const item of items) {
            rows.push({ ...item, emp_name: user.name, role_name: user.roleName });
          }
        }
        const total_minutes = rows.reduce((s, i) => s + (i.total_minutes || 0), 0);
        setReportData({ rows, total_minutes, userCount: userList.length });
      }

      if (reportType.routines) {
        const results = await Promise.all(
          userList.map(u => api.getRoutineReport(appId, { month, user_id: u.id, ...(routineIds ? { routine_ids: routineIds } : {}) })
            .then(r => ({ user: u, items: r.items || [] }))
          )
        );
        // Flat rows: one per employee × routine
        const rows = [];
        for (const { user, items } of results) {
          for (const item of items) {
            rows.push({ ...item, emp_name: user.name, role_name: user.roleName });
          }
        }
        const completed   = rows.filter(i => i.status === 'completed').length;
        const pending     = rows.filter(i => i.status === 'pending').length;
        const not_started = rows.filter(i => i.status === 'not_started').length;
        const withPct = rows.filter(i => i.completion_pct !== null);
        const avg = withPct.length > 0 ? Math.round(withPct.reduce((s, i) => s + (i.completion_pct || 0), 0) / withPct.length) : null;
        setRoutineReportData({ rows, summary: { total: rows.length, completed, pending, not_started, avg_completion: avg }, userCount: userList.length });
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [appId, reportType, month, selectedRole, selectedAreas, selectedRoutines, selectedEmployees, allEmployees, roleEmployees, roles, isEmployee, me]);

  const subtitle = monthLabel(month);
  const hasReport = reportData || routineReportData;

  // Computed summary values (rows = flat per-employee × item)
  const areaRows    = reportData ? (reportData.rows || []) : [];
  const routineRows = routineReportData ? (routineReportData.rows || []) : [];
  const uniqueAreas    = useMemo(() => [...new Set(areaRows.map(r => r.area_id))].length, [areaRows]);
  const uniqueRoutines = useMemo(() => [...new Set(routineRows.map(r => r.routine_id))].length, [routineRows]);
  const totalEmps      = reportData ? (reportData.userCount || 1) : (routineReportData?.userCount || 1);

  // Donut chart data
  const areasDone  = areaRows.reduce((s, i) => s + (i.tasks_done || 0), 0);
  const areasTotal = areaRows.reduce((s, i) => s + (i.task_count || 0), 0);
  const rComplete  = routineReportData ? (routineReportData.summary?.completed || 0) : 0;
  const rTotal2    = routineReportData ? (routineReportData.summary?.total || 0) : 0;

  const donutAreas    = reportData        ? { completed: areasDone, total: areasTotal } : null;
  const donutRoutines = routineReportData ? { completed: rComplete,  total: rTotal2  } : null;

  const maxMinutes = useMemo(() => {
    if (!reportData) return 0;
    return Math.max(...areaRows.map(i => i.total_minutes), 1);
  }, [reportData, areaRows]);

  // PDF/Excel props — flat per-employee rows
  const areasPdfProps = useMemo(() => {
    if (!reportData) return null;
    const s = routineReportData?.summary || {};
    return {
      title: 'Areas Report', subtitle,
      summaryCards: [
        { label: 'Unique Areas', value: uniqueAreas, color: '#38bdf8' },
        { label: 'Total Hours', value: formatHoursMinutes(reportData.total_minutes || 0), color: '#38bdf8' },
        { label: 'Tasks Done', value: areaRows.reduce((s, i) => s + (i.tasks_done || 0), 0), color: '#4ade80' },
        { label: 'Employees', value: reportData.userCount || 1, color: '#a78bfa' },
      ],
      table: {
        headers: ['Employee', 'Role', 'Area', 'Time Logged', 'Task Count', 'Completed', 'In Progress', 'Not Started'],
        rows: areaRows.map(i => [
          i.emp_name,
          i.role_name,
          i.area_name,
          formatHoursMinutes(i.total_minutes),
          i.task_count,
          i.tasks_done,
          i.task_count - i.tasks_done > 0 ? i.task_count - i.tasks_done : 0,
          i.tasks_done === 0 && i.task_count > 0 ? i.task_count : 0,
        ]),
      },
    };
  }, [reportData, areaRows, uniqueAreas, subtitle]);

  const routinesPdfProps = useMemo(() => {
    if (!routineReportData) return null;
    const s = routineReportData.summary || {};
    return {
      title: 'Routines Report', subtitle,
      summaryCards: [
        { label: 'Total', value: s.total || 0, color: 'var(--text-primary)' },
        { label: 'Completed', value: s.completed || 0, color: '#4ade80' },
        { label: 'In Progress', value: s.pending || 0, color: '#fbbf24' },
        { label: 'Not Started', value: s.not_started || 0, color: '#f87171' },
      ],
      table: {
        headers: ['Employee', 'Role', 'Routine', 'Period', 'Time Logged', 'Task Count', 'Completed', 'In Progress', 'Not Started'],
        rows: routineRows.map(i => [
          i.emp_name,
          i.role_name,
          i.description,
          PERIOD_LABEL[i.periodicity] || i.periodicity,
          formatHoursMinutes(i.total_minutes),
          i.tasks_total,
          i.tasks_done,
          i.status === 'pending' ? 1 : 0,
          i.status === 'not_started' ? 1 : 0,
        ]),
      },
    };
  }, [routineReportData, routineRows, subtitle]);

  const [selectionWarning, setSelectionWarning] = useState(false);

  const hasItemsSelected = (
    (!reportType.areas || selectedAreas.length > 0) &&
    (!reportType.routines || selectedRoutines.length > 0)
  );

  const canGenerate = !loading && month && selectedRole && (reportType.areas || reportType.routines);

  function requireSelection(action) {
    if (!hasItemsSelected) {
      setSelectionWarning(true);
      setTimeout(() => setSelectionWarning(false), 3000);
      return;
    }
    action();
  }

  return (
    <div className="tasks-page">
      {/* ── Page Header ── */}
      <div className="tl-header">
        <div>
<h1 className="page-title tl-title-row">Other Reports</h1>
          <p className="tl-subtitle">Generate insights for Areas &amp; Routines based on your selected criteria.</p>
        </div>
      </div>

      {/* ── Filter Card ── */}
      <div style={{
        background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
        padding: '20px 24px', marginBottom: 12,
        display: 'flex', gap: 0, alignItems: 'flex-start', flexWrap: 'wrap',
      }}>

        {/* Step 1: Role */}
        <div style={{ flex: '1 1 160px', paddingRight: 20, borderRight: '1px solid var(--border)', marginRight: 20, minWidth: 160 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <StepCircle num="1" />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Select Role</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Choose a role</div>
            </div>
          </div>
          <SimpleSelect
            value={selectedRole}
            onChange={setSelectedRole}
            options={roles.map(r => ({ value: String(r.id), label: r.title }))}
            placeholder={rolesLoading ? 'Loading...' : '-- Select Role --'}
            loading={rolesLoading}
          />
        </div>

        {/* Step 2: Employees */}
        <div style={{ flex: '1 1 200px', paddingRight: 20, borderRight: '1px solid var(--border)', marginRight: 20, minWidth: 180 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <StepCircle num="2" />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Select Employees</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{isEmployee ? 'You' : 'Choose employees'}</div>
            </div>
          </div>
          {isEmployee ? (
            <div style={{ fontSize: 13, color: 'var(--text-primary)', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input,var(--bg-muted))' }}>
              {me ? [me.first_name, me.last_name].filter(Boolean).join(' ') || me.email : 'You'}
            </div>
          ) : (
            <MultiSelectDropdown
              label="Employee"
              items={roleEmployees.map(u => ({ id: Number(u.id), name: empName(u) }))}
              selected={selectedEmployees}
              onToggle={toggleEmployee}
              onSetSelected={ids => { setAllEmployees(false); setSelectedEmployees(ids); }}
              loading={empLoading}
              emptyMsg={selectedRole ? 'No employees with this role.' : 'Select a role first.'}
              allowAll
              allLabel="All"
              allSelected={allEmployees}
              onToggleAll={toggleAllEmployees}
            />
          )}
        </div>

        {/* Step 3: Report Type */}
        <div style={{ flex: '0 0 auto', paddingRight: 20, borderRight: '1px solid var(--border)', marginRight: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <StepCircle num="3" />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Report Type</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Choose area, routines or both</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 2 }}>
            {[{ key: 'areas', label: 'Areas', color: '#38bdf8' }, { key: 'routines', label: 'Routines', color: '#4ade80' }].map(t => (
              <label key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={reportType[t.key]}
                  onChange={() => {
                    const next = { ...reportType, [t.key]: !reportType[t.key] };
                    if (!next.areas && !next.routines) return; // must have at least one
                    setReportType(next);
                  }}
                  style={{ accentColor: t.color, width: 16, height: 16, cursor: 'pointer' }}
                />
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{t.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Step 4: Month */}
        <div style={{ flex: '0 0 auto', paddingRight: 20, borderRight: '1px solid var(--border)', marginRight: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <StepCircle num="4" />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Select Month</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Choose report month</div>
            </div>
          </div>
          <select
            value={month}
            onChange={e => setMonth(e.target.value)}
            style={{
              padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)',
              fontSize: 13, minWidth: 150,
              background: 'var(--bg-input,var(--bg-muted))', color: 'var(--text-primary)',
              appearance: 'auto',
            }}
          >
            {MONTHS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* Step 5: Select Items (label only — panels below) */}
        <div style={{ flex: '0 0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <StepCircle num="5" />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Select Items</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Select areas and routines</div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', paddingTop: 4 }}>
            {selectedAreas.length + selectedRoutines.length > 0
              ? `${selectedAreas.length + selectedRoutines.length} item(s) selected`
              : 'See panels below'}
          </div>
        </div>
      </div>

      {/* Info bar */}
      <div style={{
        background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.18)',
        borderRadius: 8, padding: '9px 14px', marginBottom: 14,
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 12, color: 'var(--text-secondary)',
      }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="6" stroke="#38bdf8" strokeWidth="1.4"/>
          <path d="M7 6v4M7 4.5v.5" stroke="#38bdf8" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
        Report data is based on task completed date for completed items. Other data will be calculated as per the selected month.
      </div>

      {/* ── Selection warning banner ── */}
      {selectionWarning && (
        <div style={{
          background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.5)',
          borderRadius: 8, padding: '10px 16px', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 10,
          fontSize: 13, color: '#b45309', fontWeight: 600,
          animation: 'fadeIn 0.2s ease',
        }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
            <path d="M8 2L14.9 14H1.1L8 2Z" stroke="#b45309" strokeWidth="1.4" fill="rgba(251,191,36,0.2)" strokeLinejoin="round"/>
            <path d="M8 6v4M8 11.5v.5" stroke="#b45309" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          Please select at least one area {reportType.areas && reportType.routines ? 'and one routine' : ''} before generating the report.
        </div>
      )}

      {/* ── Area & Routine Item Panels ── */}
      {(reportType.areas || reportType.routines) && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          {reportType.areas && (
            <ItemPanel
              title="Areas"
              color="#38bdf8"
              highlight={selectionWarning && selectedAreas.length === 0}
              icon={
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <rect x="1" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                  <rect x="11" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                  <rect x="1" y="11" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                  <rect x="11" y="11" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
              }
              items={areaList}
              selected={selectedAreas}
              onToggle={id => setSelectedAreas(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
              onSelectAll={() => setSelectedAreas(areaList.map(i => i.id))}
              onClearAll={() => setSelectedAreas([])}
              loading={listLoading}
              nameKey="name"
            />
          )}
          {reportType.routines && (
            <ItemPanel
              title="Routines"
              color="#4ade80"
              highlight={selectionWarning && selectedRoutines.length === 0}
              icon={
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <line x1="3" y1="5" x2="15" y2="5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="3" y1="9" x2="15" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="3" y1="13" x2="10" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              }
              items={routineList}
              selected={selectedRoutines}
              onToggle={id => setSelectedRoutines(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
              onSelectAll={() => setSelectedRoutines(routineList.map(i => i.id))}
              onClearAll={() => setSelectedRoutines([])}
              loading={listLoading}
              nameKey="description"
            />
          )}
        </div>
      )}

      {/* ── Action bar: Generate + Export ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {hasReport && (
          <>
            <button
              onClick={() => requireSelection(() =>
                downloadCombinedExcel(
                  `Other-Reports-${new Date().toISOString().slice(0, 10)}.xls`,
                  subtitle, areasPdfProps, routinesPdfProps,
                )
              )}
              style={excelBtnStyle}
            >
              <ExcelIcon /> Excel
            </button>
            <CombinedPdfButton
              areasPdfProps={areasPdfProps}
              routinesPdfProps={routinesPdfProps}
              subtitle={subtitle}
              requireSelection={requireSelection}
            />
            <div style={{ width: 1, height: 24, background: 'var(--border)', margin: '0 4px' }} />
          </>
        )}
        <button
          onClick={() => requireSelection(loadReport)}
          disabled={!canGenerate}
          style={{
            padding: '9px 20px', borderRadius: 8, border: 'none', cursor: canGenerate ? 'pointer' : 'not-allowed',
            background: canGenerate ? 'var(--brand)' : 'var(--bg-muted)', color: canGenerate ? '#fff' : 'var(--text-muted)',
            fontWeight: 700, fontSize: 14,
            display: 'flex', alignItems: 'center', gap: 8,
            transition: 'all 0.15s',
          }}
        >
          {loading ? (
            <>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeDasharray="28" strokeDashoffset="10" />
              </svg>
              Generating...
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="10" width="3" height="4" rx="1" fill="currentColor" opacity="0.7"/>
                <rect x="6.5" y="7" width="3" height="7" rx="1" fill="currentColor" opacity="0.85"/>
                <rect x="11" y="4" width="3" height="10" rx="1" fill="currentColor"/>
              </svg>
              Generate Report
            </>
          )}
        </button>
      </div>

      {/* Errors */}
      {error && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 8, padding: '12px 16px', color: '#ef4444', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Empty / loading state */}
      {!hasReport && !loading && (
        <div style={{ padding: 56, textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ marginBottom: 12, opacity: 0.35 }}>
            <rect x="4" y="26" width="7" height="10" rx="2" fill="currentColor"/>
            <rect x="16" y="18" width="7" height="18" rx="2" fill="currentColor"/>
            <rect x="28" y="10" width="7" height="26" rx="2" fill="currentColor"/>
          </svg>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No report generated yet</div>
          <div style={{ fontSize: 13 }}>Select your criteria and click <strong style={{ color: 'var(--brand)' }}>Generate Report</strong>.</div>
        </div>
      )}

      {loading && (
        <div style={{ padding: 56, textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 14 }}>Generating report...</div>
        </div>
      )}

      {/* ── Report Output ── */}
      {!loading && hasReport && (
        <>
          {/* Completion Summary + Chart */}
          <div style={{
            display: 'flex', gap: 20, marginBottom: 20, flexWrap: 'wrap',
            background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 24px',
          }}>
            {/* Summary cards — 2×2 grid */}
            <div style={{ flex: '1 1 0', minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>Completion Summary</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {reportType.areas && (
                  <SummaryCard icon="✓" label="Area Tasks Done" value={`${areasDone} / ${areasTotal}`} color="#38bdf8" />
                )}
                {reportType.routines && (
                  <SummaryCard icon="✓" label="Routine Tasks Done" value={`${routineReportData?.summary?.completed || 0} / ${routineReportData?.summary?.total || 0}`} color="#4ade80" />
                )}
                <SummaryCard icon="👥" label="Total Employees" value={totalEmps} color="#a78bfa" />
                <SummaryCard icon="📅" label="Report Month" value={subtitle} color="#fb923c" />
              </div>
            </div>

            {/* Donut chart */}
            <div style={{
              flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14, textAlign: 'center' }}>
                Areas vs Routines Completion
              </div>
              <DonutChart areas={donutAreas} routines={donutRoutines} />
            </div>
          </div>

          {/* ── Combined Report Tables ── */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>

            {/* Areas Report */}
            {reportData && (
              <div>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 20px', borderBottom: '1px solid var(--border)',
                  background: 'rgba(56,189,248,0.05)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: '#38bdf8', fontSize: 16 }}>⊞</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Areas Report</span>
                  </div>
                  <div style={{ display: 'flex', gap: 16 }}>
                    {[
                      { label: 'Unique Areas', value: uniqueAreas, color: '#38bdf8' },
                      { label: 'Total Hours', value: formatHoursMinutes(reportData.total_minutes || 0), color: '#38bdf8' },
                      { label: 'Tasks Done', value: areaRows.reduce((s, i) => s + (i.tasks_done || 0), 0), color: '#4ade80' },
                      { label: 'Employees', value: reportData.userCount || 1, color: '#a78bfa' },
                    ].map(c => (
                      <div key={c.label} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.label}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: c.color }}>{c.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
                {areaRows.length === 0 ? (
                  <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                    No areas data found for {monthLabel(month)}.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--border)', background: 'var(--bg-muted)' }}>
                          <Th>Employee &amp; Role</Th>
                          <Th>Area</Th>
                          <Th>Time Logged</Th>
                          <Th right>Task Count</Th>
                          <Th right>Completed</Th>
                          <Th right>In Progress</Th>
                          <Th right>Not Started</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {areaRows.map((item, i) => {
                          const inProgress  = item.task_count > item.tasks_done ? item.task_count - item.tasks_done : 0;
                          const notStarted  = item.tasks_done === 0 ? item.task_count : 0;
                          return (
                            <tr key={`${item.area_id}-${i}`} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'rgba(128,128,128,0.025)' }}>
                              <td style={{ padding: '12px 14px', minWidth: 160, textAlign: 'center' }}>
                                <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{item.emp_name}</div>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{item.role_name}</div>
                              </td>
                              <td style={{ padding: '12px 14px', fontWeight: 500, color: 'var(--text-primary)', maxWidth: 200, textAlign: 'center' }}>{item.area_name}</td>
                              <td style={{ padding: '12px 14px', textAlign: 'center', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{fmtHours(item.total_minutes)}</td>
                              <td style={{ padding: '12px 14px', textAlign: 'center', color: 'var(--text-secondary)' }}>{item.task_count}</td>
                              <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 600, color: '#4ade80' }}>{item.tasks_done}</td>
                              <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 600, color: '#fbbf24' }}>{inProgress}</td>
                              <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 600, color: '#f87171' }}>{notStarted}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg-muted)' }}>
                          <td colSpan={2} style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--text-secondary)', textAlign: 'center' }}>Total</td>
                          <td style={{ padding: '10px 14px', fontWeight: 700, color: '#38bdf8', textAlign: 'center' }}>{fmtHours(reportData.total_minutes)}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: 'var(--text-secondary)' }}>{areaRows.reduce((s, i) => s + (i.task_count || 0), 0)}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#4ade80' }}>{areaRows.reduce((s, i) => s + (i.tasks_done || 0), 0)}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#fbbf24' }}>{areaRows.reduce((s, i) => s + (i.task_count > i.tasks_done ? i.task_count - i.tasks_done : 0), 0)}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#f87171' }}>{areaRows.reduce((s, i) => s + (i.tasks_done === 0 ? i.task_count : 0), 0)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Divider between tables */}
            {reportData && routineReportData && (
              <div style={{ height: 1, background: 'var(--border)' }} />
            )}

            {/* Routines Report */}
            {routineReportData && (
              <div>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 20px', borderBottom: '1px solid var(--border)',
                  background: 'rgba(74,222,128,0.05)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: '#4ade80', fontSize: 16 }}>≡</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Routines Report</span>
                  </div>
                  <div style={{ display: 'flex', gap: 16 }}>
                    {(() => {
                      const s = routineReportData.summary || {};
                      return [
                        { label: 'Total', value: s.total || 0, color: 'var(--text-primary)' },
                        { label: 'Completed', value: s.completed || 0, color: '#4ade80' },
                        { label: 'In Progress', value: s.pending || 0, color: '#fbbf24' },
                        { label: 'Not Started', value: s.not_started || 0, color: '#f87171' },
                        { label: 'Avg Completion', value: s.avg_completion != null ? `${s.avg_completion}%` : '—', color: '#38bdf8' },
                      ].map(c => (
                        <div key={c.label} style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.label}</div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: c.color }}>{c.value}</div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
                {routineRows.length === 0 ? (
                  <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                    No routines data found for {monthLabel(month)}.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--border)', background: 'var(--bg-muted)' }}>
                          <Th>Employee &amp; Role</Th>
                          <Th>Routine</Th>
                          <Th>Period</Th>
                          <Th>Time Logged</Th>
                          <Th right>Task Count</Th>
                          <Th right>Completed</Th>
                          <Th right>In Progress</Th>
                          <Th right>Not Started</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {routineRows.map((item, i) => (
                          <tr key={`${item.routine_id}-${i}`} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'rgba(128,128,128,0.025)' }}>
                            <td style={{ padding: '12px 14px', minWidth: 160 }}>
                              <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{item.emp_name}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{item.role_name}</div>
                            </td>
                            <td style={{ padding: '12px 14px', fontWeight: 500, color: 'var(--text-primary)', maxWidth: 240, wordBreak: 'break-word', whiteSpace: 'normal', textAlign: 'center' }}>{item.description}</td>
                            <td style={{ padding: '12px 14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', textAlign: 'center' }}>{PERIOD_LABEL[item.periodicity] || item.periodicity}</td>
                            <td style={{ padding: '12px 14px', whiteSpace: 'nowrap', color: 'var(--text-secondary)', textAlign: 'center' }}>{fmtHours(item.total_minutes)}</td>
                            <td style={{ padding: '12px 14px', textAlign: 'center', color: 'var(--text-secondary)' }}>{item.tasks_total}</td>
                            <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 600, color: '#4ade80' }}>{item.tasks_done}</td>
                            <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 600, color: '#fbbf24' }}>{item.status === 'pending' ? 1 : 0}</td>
                            <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 600, color: '#f87171' }}>{item.status === 'not_started' ? 1 : 0}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg-muted)' }}>
                          <td colSpan={3} style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--text-secondary)', textAlign: 'center' }}>Total</td>
                          <td style={{ padding: '10px 14px', fontWeight: 700, color: '#38bdf8', textAlign: 'center' }}>{fmtHours(routineRows.reduce((s, i) => s + (i.total_minutes || 0), 0))}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: 'var(--text-secondary)' }}>{routineRows.reduce((s, i) => s + (i.tasks_total || 0), 0)}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#4ade80' }}>{routineRows.reduce((s, i) => s + (i.tasks_done || 0), 0)}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#fbbf24' }}>{routineRows.filter(i => i.status === 'pending').length}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#f87171' }}>{routineRows.filter(i => i.status === 'not_started').length}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
      `}</style>
    </div>
  );
}
